import { SpaceType } from "@components/atoms/Spacer/types";
import CustomWeb from "@components/atoms/Webview";
import { View, DeviceEventEmitter } from "react-native";
import styled from "styled-components/native";
import { useCallback, useEffect, useState } from "react";
import { useUserInfo } from "@redux/slices/userInfoSlice";

interface IFrameSectionProps {
  data: any;
  containerWidth?: number;
  theme: any;
}

let globalResetTimestamp: number = 0;
let listenerAdded = false;

if (!listenerAdded) {
  listenerAdded = true;
  DeviceEventEmitter.addListener("RESET_FILTERS_EVENT", () => {
    globalResetTimestamp = Date.now();
  });
}

const IFrameSection = ({ data, containerWidth, theme }: IFrameSectionProps) => {
  const mHeight = theme.isMobile
    ? data?.height?.mobile
    : theme.isTablet
    ? data?.height?.tablet
    : theme.isLargeScreen
    ? data?.height?.desktop
    : 600;

  const mContainerWidth = (containerWidth || 300) + SpaceType.Big;
  const injectedCSS = `
    .divbtnpos,.hPHUXe, .sliderHeight {
      display: none !important;
    }
  `;
  const userInfo = useUserInfo();
  const userScope = userInfo?.user?.updated_at
    ? encodeURIComponent(userInfo.user.updated_at)
    : "anonymous";

  const [resetKey, setResetKey] = useState<number>(globalResetTimestamp);

  useEffect(() => {
    const listener = DeviceEventEmitter.addListener(
      "RESET_FILTERS_EVENT",
      () => {
        setResetKey(globalResetTimestamp);
      }
    );
    return () => listener.remove();
  }, []);

  useEffect(() => {
    if (globalResetTimestamp > 0) {
      const age = Date.now() - globalResetTimestamp;
      if (age > 2000) {
        setResetKey(0);
        globalResetTimestamp = 0;
      }
    }
  }, []);

  const baseQuery = `userScope=${userScope}`;
  const resetQuery = resetKey ? `reset=true&ts=${resetKey}` : "";

  const extraQuery = resetQuery ? `${baseQuery}&${resetQuery}` : baseQuery;

  useEffect(() => {
    if (extraQuery && resetKey) {
      const timeoutId = setTimeout(() => {
        setResetKey(0);
        globalResetTimestamp = 0;
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [extraQuery, resetKey]);

  const onWebMessage = useCallback((event: any) => {
    try {
      const parsed = JSON.parse(event?.nativeEvent?.data);

      if (parsed?.type === "DIAMOND_FILTER_UPDATE") {
        const iframeData = parsed?.data;

        const normalizedPayload = {
          color: iframeData?.color,
          shapes: iframeData?.shape ? [iframeData.shape] : [],
          meas_length_from: iframeData?.mm?.min ?? 0,
          meas_length_to: iframeData?.mm?.max ?? 100,
          size_from: iframeData?.carat?.min ?? 0,
          size_to: iframeData?.carat?.max ?? 1000,
        };

        DeviceEventEmitter.emit("IFRAME_FILTER_PAYLOAD", normalizedPayload);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  return (
    <Container>
      <View
        style={{
          width: "100%",
          height: mHeight,
          paddingBottom: 2,
          paddingTop: 2,
        }}
      >
        <CustomWeb
          url={data?.hyperlink}
          extraQuery={extraQuery}
          isFromPDP={false}
          allowScaleScript={false}
          showLoader={true}
          width={mContainerWidth}
          injectedCSS={injectedCSS}
          onMessage={onWebMessage}
        />
      </View>
    </Container>
  );
};

const Container = styled.View`
  flex: 1;
  flex-basis: auto;
`;

export default IFrameSection;
